import type { InputMaterialWithQuantity } from "../Machine";
import {
  Board,
  BoardEnd,
  BoardEnds,
  boardEnds,
  JIG_GRADE_KINDS,
  MaterialInstance,
  MITER_ANGLES,
  panelWidth,
  RACK_GRADE_KINDS,
  REAL_WOOD_SPECIES,
  SHOP_FURNITURE_KINDS,
  SignedMiterAngle,
  SURFACE_CONDITIONS,
  SurfaceCondition,
} from "../Materials";
import { SKILL_IDS, SkillId } from "../Skill";
import type { ToolId } from "../Tool";
import { PLAN_IDS, PlanId, RAW_PLAN_RECORDS } from "../plans";

/**
 * A plan record: one product or piece of shop equipment, as authored —
 * the JSON files in `src/game/plans/`, one per build. A record is the
 * single artifact behind an assembly: its geometry (where every part
 * lies and where every fastener goes), the stock each part is cut from,
 * and the facts the shop needs around it (what skill unlocks it, how
 * long the build takes, what it sells for, which drawer it files in).
 * `blueprint.ts` turns a record into the runtime blueprint the bench
 * assembles on and the sprite draws from; `plan-operations.ts` turns it
 * into the operation a bench offers.
 *
 * Records are data on purpose: the blueprint editor (`/blueprints.html`
 * on the dev server) reads and writes them, so nothing in a record may
 * need code to express. In particular:
 *
 *  - A slot's stock is declarative (`StockRecord`), and the matcher the
 *    bench uses (`stockRequirement`) is derived from it and the part's
 *    nominal size. A dimension the stock leaves out is exactly the
 *    part's nominal; a list is the allowed values; "any" leaves it
 *    unconstrained. Sheet stock is stated the way sheets are stored,
 *    long side as the length, whichever way the part lies.
 *  - End treatments are constraints too: a part drawn with mitered ends
 *    takes only boards whose ends can be turned to match (`orientEnds`).
 *    Square nominal ends constrain nothing — a mitered offcut can still
 *    be a crate wall.
 *  - A part is a box: its footprint (`xIn`, `yIn`, `angleDeg`), its
 *    stance (flat, on edge, on end — which decides how tall it stands),
 *    and the height of its underside above the bench (`zIn`). Nothing
 *    else about where it is exists to be stored: draw order, what laps
 *    what, and what a nail passes through are all read off the boxes
 *    (`plan-geometry.ts`).
 *  - Fasteners are stored in full, never derived at load: every one's
 *    head position, and whether it is driven straight down or level
 *    from the side. The editor's auto-place (`deriveFasteners`) fills
 *    the list in; what's saved is exactly what the bench arms and the
 *    sprite draws. Which two parts a fastener joins is *not* stored —
 *    it's the first two boxes its shank passes through, derived when
 *    the blueprint is built (`fastenerJoins`), so the record can't
 *    contradict its own geometry.
 *
 * `parsePlanRecord` is the whole schema: it rejects anything a record
 * can't say, at module load and again when the editor pastes one in.
 *
 * This module imports only vocabularies (Materials, Skill, the raw plan
 * files) on purpose: the tools' operation lists are built from these
 * records at their own module load (plan-operations.ts), and anything
 * here that reached back through the tool registry would make that
 * load order-dependent.
 */

export const PLAN_CATEGORIES = [
  { id: "rustic", label: "Rustic Projects" },
  { id: "fineGoods", label: "Fine Goods" },
  { id: "furniture", label: "Furniture" },
  { id: "shopFurniture", label: "Shop Furniture" },
  { id: "jigs", label: "Jigs" },
] as const;

export type PlanCategoryId = (typeof PLAN_CATEGORIES)[number]["id"];

export function planCategoryLabel(id: PlanCategoryId): string {
  const category = PLAN_CATEGORIES.find((entry) => entry.id === id);
  return category ? category.label : id;
}

/** What a build leaves behind: a product on the bench, or a piece of
 * shop equipment granted straight to the shop. */
export const PLAN_BUILDS = ["product", "tool", "machine", "upgrade"] as const;
export type PlanBuilds = (typeof PLAN_BUILDS)[number];

/** The supplies a build can be fastened with. Nails take the hammer,
 * screws the drill (`fastenerToolId`). */
export const FASTENER_IDS = ["nails", "screws"] as const;
export type FastenerId = (typeof FASTENER_IDS)[number];

/** Which woods a slot takes: reclaimed pallet stock, anything better,
 * or (omitted) either. */
export const STOCK_SPECIES = ["realWood", "pallet"] as const;
export type StockSpecies = (typeof STOCK_SPECIES)[number];

/** The sheet grades a slot can ask for — the kind lists in Materials.ts,
 * by the work they're fit for. */
export const SHEET_GRADES = {
  jig: JIG_GRADE_KINDS,
  shopFurniture: SHOP_FURNITURE_KINDS,
  rack: RACK_GRADE_KINDS,
} as const;
export type SheetGrade = keyof typeof SHEET_GRADES;

export const STOCK_TYPES = ["board", "panel", "plywood"] as const;
export type StockType = (typeof STOCK_TYPES)[number];

/** A dimension constraint: omitted is exactly the part's nominal, a
 * list is the allowed values, "any" is unconstrained. */
export type StockDimension = ReadonlyArray<number> | "any";

/** What fills a slot, declared. */
export interface StockRecord {
  readonly type: StockType;
  readonly species?: StockSpecies;
  /** The least finished surface that qualifies. */
  readonly surface?: SurfaceCondition;
  /** Plywood only. Omitted: any sheet kind. */
  readonly sheetGrade?: SheetGrade;
  /** Inches. */
  readonly length?: StockDimension;
  /** Inches (boards); a panel's width is always its part's. */
  readonly width?: StockDimension;
  /** Quarters of an inch. */
  readonly thickness?: StockDimension;
}

/** One part's place in the finished product. */
export interface SlotRecord {
  /** Stable id, `role-index` by convention — recorded on the product's
   * parts and named by the fasteners that join it. */
  readonly id: string;
  /** What the part is in prose: "rail", "shelf". */
  readonly role: string;
  /** The part's nominal dims, for ghosts, overlap math, and stand-in
   * parts on products from older saves (Board units). */
  readonly part: {
    readonly widthIn: number;
    readonly lengthIn: number;
    readonly thicknessQ: number;
    /** The end treatments the part shows lying in this slot, signed the
     * way the finished piece needs them — a frame rail's long edge must
     * face the frame's outside for the corners to close. A consumed
     * board whose ends match in any orientation is turned on the way in
     * (`orientEnds`); stand-in parts for older saves synthesize exactly
     * these. */
    readonly ends?: BoardEnds;
  };
  readonly stock: StockRecord;
  /** Part center in product inches from the product's top-left corner. */
  readonly xIn: number;
  readonly yIn: number;
  /** Degrees turned from vertical (length running down). */
  readonly angleDeg: number;
  /** How high the part's underside sits above the bench, in inches: 0
   * lies on the bench, a slat across two rails sits at the rails' top.
   * How tall it stands from there follows from its stance
   * (`slotHeightIn`). */
  readonly zIn: number;
  /** The part stands on its long edge — a rail or joist — so its
   * footprint is its thickness, not its width, and the bench only seats
   * a piece that has been tipped up to match (F in the bench view). */
  readonly onEdge?: boolean;
  /** The part stands on its end — a table leg against the face-down
   * top — so its footprint is its bare cross-section (width across,
   * thickness deep), and only a piece stood on end (F twice) seats. */
  readonly onEnd?: boolean;
}

/** How a fastener is driven level, from the side of the build. */
export interface LevelDrive {
  /** The way the shank travels in plan, in the slot-angle convention:
   * 0 down the sheet (+y), 90 to the left (−x). */
  readonly headingDeg: number;
  /** The shank's height above the bench, in inches. Two nails into one
   * board end differ only here. */
  readonly zIn: number;
}

/**
 * One fastener — like a pallet nail, it joins the two parts its shank
 * passes through and nothing else. `xIn`/`yIn` is where the head sits:
 * driven straight down (no `level`), that is a point on the top face of
 * the uppermost part there; driven level, a point on the outer face the
 * shank enters, on its way into the end or side of the part behind.
 */
export interface FastenerRecord {
  readonly xIn: number;
  readonly yIn: number;
  /** Present when the fastener is driven level from the side; absent
   * when it is driven straight down from above. */
  readonly level?: LevelDrive;
}

export interface PlanRecord {
  /** Registry key; for a product it is the product type. */
  readonly id: PlanId;
  /** What the plan builds, as the drawing is titled: "Shelf". The
   * bench's operation reads "Build Shelf" (plan-operations.ts). */
  readonly name: string;
  readonly builds: PlanBuilds;
  /** The drawer divider the plan files under. */
  readonly category: PlanCategoryId;
  /** The pencil note in the drawing's margin — the one place
   * personality belongs (see the voice rules in docs/shop-manual.md). */
  readonly note: string;
  readonly skill: SkillId;
  /** Shop minutes of hand work. */
  readonly duration: number;
  /** Whose operation list carries the build: the bench's own, or a
   * mounted tool's — a station without that tool can't select it. */
  readonly offeredBy: "bench" | ToolId;
  readonly fastener: FastenerId;
  /** Base sell value, products only. */
  readonly value?: number;
  /** Product footprint, in inches (drawn lying flat, like on the bench). */
  readonly widthIn: number;
  readonly heightIn: number;
  readonly slots: ReadonlyArray<SlotRecord>;
  readonly fasteners: ReadonlyArray<FastenerRecord>;
}

// ---------------------------------------------------------------------
// Validation

class PlanRecordError extends Error {
  constructor(source: string, message: string) {
    super(`Plan record ${source}: ${message}`);
  }
}

type Raw = Record<string, unknown>;

function isRaw(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function checkOneOf<T extends string>(
  source: string,
  field: string,
  value: unknown,
  allowed: ReadonlyArray<T>,
): T {
  if (!allowed.includes(value as T)) {
    throw new PlanRecordError(
      source,
      `${field} must be one of ${allowed.join(", ")}, got ${JSON.stringify(value)}`,
    );
  }
  return value as T;
}

function checkNumber(
  source: string,
  field: string,
  value: unknown,
  options: { min?: number; integer?: boolean } = {},
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new PlanRecordError(source, `${field} must be a number`);
  }
  if (options.min !== undefined && value < options.min) {
    throw new PlanRecordError(
      source,
      `${field} must be at least ${options.min}`,
    );
  }
  if (options.integer && !Number.isInteger(value)) {
    throw new PlanRecordError(source, `${field} must be a whole number`);
  }
  return value;
}

function checkString(source: string, field: string, value: unknown): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new PlanRecordError(source, `${field} must be a non-empty string`);
  }
  return value;
}

function checkOptionalBoolean(
  source: string,
  field: string,
  value: unknown,
): boolean | undefined {
  if (value !== undefined && typeof value !== "boolean") {
    throw new PlanRecordError(source, `${field} must be true or false`);
  }
  return value;
}

function checkDimension(
  source: string,
  field: string,
  value: unknown,
): StockDimension | undefined {
  if (value === undefined || value === "any") {
    return value;
  }
  if (!Array.isArray(value) || value.length === 0) {
    throw new PlanRecordError(
      source,
      `${field} must be "any" or a non-empty list of numbers`,
    );
  }
  return value.map((entry, i) =>
    checkNumber(source, `${field}[${i}]`, entry, { min: 0 }),
  );
}

function checkEnd(source: string, field: string, value: unknown): BoardEnd {
  if (!isRaw(value)) {
    throw new PlanRecordError(source, `${field} must be an end`);
  }
  const kind = checkOneOf(source, `${field}.kind`, value.kind, [
    "square",
    "mitered",
  ]);
  if (kind === "square") {
    return { kind };
  }
  const angle = checkNumber(source, `${field}.angle`, value.angle);
  if (!(MITER_ANGLES as ReadonlyArray<number>).includes(Math.abs(angle))) {
    throw new PlanRecordError(
      source,
      `${field}.angle must be one of the saw's stops (±${MITER_ANGLES.join(", ±")})`,
    );
  }
  return { kind, angle: angle as SignedMiterAngle };
}

function checkStock(source: string, raw: unknown): StockRecord {
  if (!isRaw(raw)) {
    throw new PlanRecordError(source, "stock must be an object");
  }
  const type = checkOneOf(source, "stock.type", raw.type, STOCK_TYPES);
  const stock: StockRecord = {
    type,
    ...(raw.species !== undefined
      ? {
          species: checkOneOf(
            source,
            "stock.species",
            raw.species,
            STOCK_SPECIES,
          ),
        }
      : {}),
    ...(raw.surface !== undefined
      ? {
          surface: checkOneOf(
            source,
            "stock.surface",
            raw.surface,
            SURFACE_CONDITIONS,
          ),
        }
      : {}),
    ...(raw.sheetGrade !== undefined
      ? {
          sheetGrade: checkOneOf(
            source,
            "stock.sheetGrade",
            raw.sheetGrade,
            Object.keys(SHEET_GRADES) as ReadonlyArray<SheetGrade>,
          ),
        }
      : {}),
  };
  const length = checkDimension(source, "stock.length", raw.length);
  const width = checkDimension(source, "stock.width", raw.width);
  const thickness = checkDimension(source, "stock.thickness", raw.thickness);
  if (type === "plywood" && (stock.species || stock.surface)) {
    throw new PlanRecordError(source, "sheet stock has no species or surface");
  }
  if (type !== "plywood" && stock.sheetGrade) {
    throw new PlanRecordError(source, "only sheet stock has a grade");
  }
  if (type === "panel" && width !== undefined) {
    throw new PlanRecordError(
      source,
      "a panel slot's width is its part's — leave stock.width out",
    );
  }
  return {
    ...stock,
    ...(length !== undefined ? { length } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(thickness !== undefined ? { thickness } : {}),
  };
}

function checkSlot(source: string, raw: unknown, index: number): SlotRecord {
  const at = `slots[${index}]`;
  if (!isRaw(raw)) {
    throw new PlanRecordError(source, `${at} must be an object`);
  }
  const id = checkString(source, `${at}.id`, raw.id);
  const role = checkString(source, `${at}.role`, raw.role);
  if (!isRaw(raw.part)) {
    throw new PlanRecordError(source, `${at}.part must be an object`);
  }
  const part = {
    widthIn: checkNumber(source, `${at}.part.widthIn`, raw.part.widthIn, {
      min: 0,
    }),
    lengthIn: checkNumber(source, `${at}.part.lengthIn`, raw.part.lengthIn, {
      min: 0,
    }),
    thicknessQ: checkNumber(
      source,
      `${at}.part.thicknessQ`,
      raw.part.thicknessQ,
      { min: 0 },
    ),
    ...(raw.part.ends !== undefined
      ? {
          ends: (() => {
            if (!isRaw(raw.part.ends)) {
              throw new PlanRecordError(
                source,
                `${at}.part.ends must have a left and a right`,
              );
            }
            return {
              left: checkEnd(
                source,
                `${at}.part.ends.left`,
                raw.part.ends.left,
              ),
              right: checkEnd(
                source,
                `${at}.part.ends.right`,
                raw.part.ends.right,
              ),
            };
          })(),
        }
      : {}),
  };
  const stock = checkStock(`${source} ${at}`, raw.stock);
  const onEdge = checkOptionalBoolean(source, `${at}.onEdge`, raw.onEdge);
  const onEnd = checkOptionalBoolean(source, `${at}.onEnd`, raw.onEnd);
  if (onEdge && onEnd) {
    throw new PlanRecordError(source, `${at} can't stand on edge and on end`);
  }
  if ((onEdge || onEnd) && stock.type !== "board") {
    throw new PlanRecordError(source, `${at}: only a board stands up`);
  }
  return {
    id,
    role,
    part,
    stock,
    xIn: checkNumber(source, `${at}.xIn`, raw.xIn),
    yIn: checkNumber(source, `${at}.yIn`, raw.yIn),
    angleDeg: checkNumber(source, `${at}.angleDeg`, raw.angleDeg),
    zIn: checkNumber(source, `${at}.zIn`, raw.zIn, { min: 0 }),
    ...(onEdge ? { onEdge } : {}),
    ...(onEnd ? { onEnd } : {}),
  };
}

function checkFastener(
  source: string,
  raw: unknown,
  index: number,
): FastenerRecord {
  const at = `fasteners[${index}]`;
  if (!isRaw(raw)) {
    throw new PlanRecordError(source, `${at} must be an object`);
  }
  const level = (() => {
    if (raw.level === undefined) return undefined;
    if (!isRaw(raw.level)) {
      throw new PlanRecordError(
        source,
        `${at}.level must give a heading and a height`,
      );
    }
    return {
      headingDeg: checkNumber(
        source,
        `${at}.level.headingDeg`,
        raw.level.headingDeg,
      ),
      zIn: checkNumber(source, `${at}.level.zIn`, raw.level.zIn, { min: 0 }),
    };
  })();
  return {
    xIn: checkNumber(source, `${at}.xIn`, raw.xIn),
    yIn: checkNumber(source, `${at}.yIn`, raw.yIn),
    ...(level ? { level } : {}),
  };
}

/**
 * The record a raw JSON value describes, or a thrown error naming the
 * first thing wrong with it. `source` labels the error ("shelf.json",
 * "the pasted plan").
 */
export function parsePlanRecord(raw: unknown, source: string): PlanRecord {
  if (!isRaw(raw)) {
    throw new PlanRecordError(source, "must be an object");
  }
  const id = checkString(source, "id", raw.id);
  if (!/^[a-z][A-Za-z0-9]*$/.test(id)) {
    throw new PlanRecordError(source, `id must be lowerCamelCase, got ${id}`);
  }
  const builds = checkOneOf(source, "builds", raw.builds, PLAN_BUILDS);
  const value =
    raw.value === undefined
      ? undefined
      : checkNumber(source, "value", raw.value, { min: 0 });
  if (builds === "product" && value === undefined) {
    throw new PlanRecordError(source, "a product needs a value");
  }
  if (builds !== "product" && value !== undefined) {
    throw new PlanRecordError(source, "only a product has a value");
  }
  if (!Array.isArray(raw.slots) || raw.slots.length === 0) {
    throw new PlanRecordError(source, "slots must be a non-empty list");
  }
  const slots = raw.slots.map((slot, i) => checkSlot(source, slot, i));
  const slotIds = new Set<string>();
  for (const slot of slots) {
    if (slotIds.has(slot.id)) {
      throw new PlanRecordError(source, `two slots share the id ${slot.id}`);
    }
    slotIds.add(slot.id);
  }
  if (!Array.isArray(raw.fasteners)) {
    throw new PlanRecordError(source, "fasteners must be a list");
  }
  const fasteners = raw.fasteners.map((fastener, i) =>
    checkFastener(source, fastener, i),
  );
  const offeredBy = checkString(source, "offeredBy", raw.offeredBy) as
    "bench" | ToolId;
  return {
    id: id as PlanId,
    name: checkString(source, "name", raw.name),
    builds,
    category: checkOneOf(
      source,
      "category",
      raw.category,
      PLAN_CATEGORIES.map((category) => category.id),
    ),
    note: checkString(source, "note", raw.note),
    skill: checkOneOf(source, "skill", raw.skill, SKILL_IDS),
    duration: checkNumber(source, "duration", raw.duration, {
      min: 1,
      integer: true,
    }),
    offeredBy,
    fastener: checkOneOf(source, "fastener", raw.fastener, FASTENER_IDS),
    ...(value !== undefined ? { value } : {}),
    widthIn: checkNumber(source, "widthIn", raw.widthIn, { min: 0 }),
    heightIn: checkNumber(source, "heightIn", raw.heightIn, { min: 0 }),
    slots,
    fasteners,
  };
}

/** Every plan, parsed once at load. A file that doesn't parse fails the
 * whole module, loudly — a bad record would otherwise surface as a
 * plan missing from the drawer. */
export const PLAN_RECORDS: Readonly<Record<PlanId, PlanRecord>> = (() => {
  const records = {} as Record<PlanId, PlanRecord>;
  for (const id of PLAN_IDS) {
    const record = parsePlanRecord(RAW_PLAN_RECORDS[id], `${id}.json`);
    if (record.id !== id) {
      throw new PlanRecordError(`${id}.json`, `declares the id ${record.id}`);
    }
    records[id] = record;
  }
  return records;
})();

// ---------------------------------------------------------------------
// End treatments

const negateEnd = (end: BoardEnd): BoardEnd =>
  end.kind === "mitered"
    ? // The signed-angle union is symmetric, so a negation stays in it
      { kind: "mitered", angle: -end.angle as SignedMiterAngle }
    : end;

const endsEqual = (a: BoardEnd, b: BoardEnd): boolean =>
  a.kind === b.kind &&
  (a.kind !== "mitered" || b.kind !== "mitered" || a.angle === b.angle);

/**
 * A board's ends as they'd read after turning it to match the nominal
 * ends, or null when no turn gets there. A board on the bench can be
 * flipped over (which negates both ends at once) and turned end for end
 * (which swaps them), and either costs nothing — so a frame rail cut
 * with either swing of the saw head seats with its long edge out, and
 * a lean-to wall's lone miter lands at the roof whichever end it was
 * cut on. A parallelogram never turns into a frame rail: its ends keep
 * the same sign in every orientation.
 */
export function orientEnds(
  ends: BoardEnds,
  nominal: BoardEnds,
): BoardEnds | null {
  const candidates: ReadonlyArray<BoardEnds> = [
    ends,
    { left: negateEnd(ends.left), right: negateEnd(ends.right) },
    { left: ends.right, right: ends.left },
    { left: negateEnd(ends.right), right: negateEnd(ends.left) },
  ];
  return (
    candidates.find(
      (candidate) =>
        endsEqual(candidate.left, nominal.left) &&
        endsEqual(candidate.right, nominal.right),
    ) ?? null
  );
}

/** Whether nominal ends constrain a board at all: square ends don't. */
function endsConstrain(ends: BoardEnds | undefined): ends is BoardEnds {
  return (
    !!ends && (ends.left.kind !== "square" || ends.right.kind !== "square")
  );
}

/** What the nominal ends demand, in shop language, for the plan sheet. */
export function describeEnds(ends: BoardEnds): string {
  const { left, right } = ends;
  if (left.kind === "mitered" && right.kind === "mitered") {
    const magnitude = Math.abs(left.angle);
    const both =
      Math.abs(right.angle) === magnitude
        ? `${magnitude}°`
        : `${magnitude}° and ${Math.abs(right.angle)}°`;
    return Math.sign(left.angle) === Math.sign(right.angle)
      ? `${both} both ends, parallel`
      : `${both} both ends, mirrored`;
  }
  const mitered = left.kind === "mitered" ? left : right;
  return mitered.kind === "mitered"
    ? `one end mitered ${Math.abs(mitered.angle)}°`
    : "square ends";
}

// ---------------------------------------------------------------------
// The derived requirement

/** The surfaces at or above a floor on the ladder. */
function surfacesFrom(
  floor: SurfaceCondition,
): ReadonlyArray<SurfaceCondition> {
  return SURFACE_CONDITIONS.slice(SURFACE_CONDITIONS.indexOf(floor));
}

function dimension(
  key: "length" | "width" | "thickness",
  declared: StockDimension | undefined,
  nominal: number,
): Partial<Record<typeof key, ReadonlyArray<number>>> {
  if (declared === "any") {
    return {};
  }
  return { [key]: declared ?? [nominal] };
}

/**
 * The matcher a slot's stock declaration comes to — the same
 * `InputMaterial` shape every recipe uses, so the bench, the plan sheet,
 * and the claim all read one requirement. Built once per blueprint.
 */
export function stockRequirement(
  slot: SlotRecord,
): InputMaterialWithQuantity<Board> {
  const { stock, part } = slot;
  const species =
    stock.species === "realWood"
      ? { species: REAL_WOOD_SPECIES }
      : stock.species === "pallet"
        ? { species: ["pallet" as const] }
        : {};
  const surface = stock.surface ? { surface: surfacesFrom(stock.surface) } : {};

  switch (stock.type) {
    case "plywood": {
      // Sheets are stated long side first, whichever way the part lies
      const longIn = Math.max(part.widthIn, part.lengthIn);
      const shortIn = Math.min(part.widthIn, part.lengthIn);
      return {
        type: ["plywood"],
        ...(stock.sheetGrade
          ? { kind: [...SHEET_GRADES[stock.sheetGrade]] }
          : {}),
        ...dimension("length", stock.length, longIn),
        ...dimension("width", stock.width, shortIn),
        ...dimension("thickness", stock.thickness, part.thicknessQ),
        quantity: 1,
      } as unknown as InputMaterialWithQuantity<Board>;
    }
    case "panel": {
      const realWood = stock.species === "realWood";
      const notes = [
        `${part.widthIn}" wide`,
        ...(realWood ? ["real wood"] : []),
        ...(stock.species === "pallet" ? ["pallet wood"] : []),
      ];
      return {
        type: ["panel"],
        ...surface,
        ...dimension("length", stock.length, part.lengthIn),
        ...dimension("thickness", stock.thickness, part.thicknessQ),
        // A panel's width lives in its strip list, so it's a predicate
        matches: (material: MaterialInstance) =>
          material.type === "panel" &&
          panelWidth(material) === part.widthIn &&
          (!stock.species ||
            material.strips.every((strip) =>
              realWood
                ? strip.species !== "pallet"
                : strip.species === "pallet",
            )),
        matchesNote: notes.join(", "),
        quantity: 1,
      } as unknown as InputMaterialWithQuantity<Board>;
    }
    case "board": {
      const nominalEnds = part.ends;
      const ends = endsConstrain(nominalEnds)
        ? {
            matches: (material: MaterialInstance) =>
              material.type === "board" &&
              orientEnds(boardEnds(material), nominalEnds) !== null,
            matchesNote: describeEnds(nominalEnds),
          }
        : {};
      return {
        type: ["board"],
        ...species,
        ...surface,
        ...dimension("length", stock.length, part.lengthIn),
        ...dimension("width", stock.width, part.widthIn),
        ...dimension("thickness", stock.thickness, part.thicknessQ),
        ...ends,
        quantity: 1,
      } as InputMaterialWithQuantity<Board>;
    }
  }
}
